import type { ReactNode } from 'react';
import { AceAvatarFramesGallery } from '@/components/help-center/aces/AceAvatarFramesGallery';
import { AceLevelTimeline } from '@/components/help-center/aces/AceLevelTimeline';
import { AcePawnsSection } from '@/components/help-center/aces/AcePawnsSection';
import { AcePstyleSection } from '@/components/help-center/aces/AcePstyleSection';
import { formatAceDisplayName } from '@/components/help-center/aces/ace-shared';
import { FAQAccordion } from '@/components/help-center/aces/FAQAccordion';
import { resolveAceAvatarFrames } from '@/lib/ace-avatar-frames';
import type { AceInfo } from '@/lib/aces-data';
import { isLikelyHtmlContent } from '@/lib/cms-rich-helpers';
import { decodeHtmlEntities } from '@/lib/cms-html';
import { cn } from '@/lib/utils';
import {
  BookOpen,
  Frame,
  HelpCircle,
  Layers,
  ListChecks,
} from 'lucide-react';

type SectionHeaderProps = {
  icon: ReactNode;
  eyebrow: string;
  title: string;
  className?: string;
};

function SectionHeader({ icon, eyebrow, title, className }: SectionHeaderProps) {
  return (
    <header className={cn('mb-8', className)}>
      <div className="mb-3 flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-50 text-amber-600">
          {icon}
        </div>
        <p className="font-poppins text-xs font-semibold uppercase tracking-widest text-amber-600">
          {eyebrow}
        </p>
      </div>
      <h2 className="font-poppins text-2xl font-bold text-gray-900 md:text-3xl">{title}</h2>
    </header>
  );
}

type AceDetailSectionsProps = {
  ace: AceInfo;
};

export function AceDetailSections({ ace }: AceDetailSectionsProps) {
  const avatarFrames = resolveAceAvatarFrames(ace);
  const aceDisplayName = formatAceDisplayName(ace.slug);

  return (
    <div className="space-y-0">
      <section className="border-b border-gray-100 bg-white py-14">
        <div className="mx-auto max-w-7xl px-6">
          <SectionHeader
            icon={<BookOpen className="h-4 w-4" />}
            eyebrow="Sobre"
            title={`O que é o ${aceDisplayName}?`}
          />
          {isLikelyHtmlContent(ace.about) ? (
            <div
              className={cn(
                'max-w-3xl font-inter text-base leading-relaxed text-gray-600',
                '[&_p]:my-3 [&_ul]:my-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-3 [&_ol]:list-decimal [&_ol]:pl-5',
                '[&_a]:text-amber-700 [&_a]:underline [&_blockquote]:border-l-4 [&_blockquote]:border-amber-300 [&_blockquote]:pl-4'
              )}
              dangerouslySetInnerHTML={{ __html: ace.about }}
            />
          ) : (
            <p className="max-w-3xl font-inter text-base leading-relaxed text-gray-600">
              {decodeHtmlEntities(ace.about)}
            </p>
          )}
        </div>
      </section>

      {avatarFrames ? (
        <section className="border-b border-gray-100 bg-gray-50 py-14">
          <div className="mx-auto max-w-7xl px-6">
            <SectionHeader
              icon={<Frame className="h-4 w-4" />}
              eyebrow="Molduras"
              title="Avatar frames"
            />
            <p className="mb-8 max-w-3xl font-inter text-sm leading-relaxed text-gray-600 md:text-base">
              Molduras disponíveis para assinantes deste Ace, conforme documentado na wiki do xat.
              Aplique-as em Definições da conta → Avatar → Frame.
            </p>
            <AceAvatarFramesGallery frames={avatarFrames} />
          </div>
        </section>
      ) : null}

      <section className="border-b border-gray-100 bg-gray-50 py-14">
        <div className="mx-auto max-w-7xl px-6">
          <SectionHeader
            icon={<ListChecks className="h-4 w-4" />}
            eyebrow="Como funciona"
            title="Passo a passo"
          />
          <ol className="grid gap-4 md:grid-cols-2">
            {ace.howItWorks.map((step) => (
              <li
                key={step.step}
                className="flex gap-4 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm transition-colors hover:border-amber-200/70"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#030023] to-[#000b6a] font-poppins text-sm font-bold text-white">
                  {step.step}
                </span>
                <div>
                  <h3 className="font-poppins text-base font-semibold text-gray-900">
                    {step.title}
                  </h3>
                  <p className="mt-1 font-inter text-sm leading-relaxed text-gray-600">
                    {step.description}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <AcePstyleSection ace={ace} />

      <AcePawnsSection slug={ace.slug} />

      {ace.levels && ace.levels.length > 0 ? (
        <section className="border-b border-gray-100 bg-white py-14">
          <div className="mx-auto max-w-7xl px-6">
            <SectionHeader
              icon={<Layers className="h-4 w-4" />}
              eyebrow="Níveis"
              title="Progressão da assinatura"
            />
            <AceLevelTimeline levels={ace.levels} />
          </div>
        </section>
      ) : null}

      <section className="bg-white py-14">
        <div className="mx-auto max-w-7xl px-6">
          <SectionHeader
            icon={<HelpCircle className="h-4 w-4" />}
            eyebrow="FAQ"
            title="Perguntas frequentes"
          />
          <FAQAccordion items={ace.faq} />
        </div>
      </section>
    </div>
  );
}
